import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

import './styles.css';
import { Boleto } from './interfaces';
import { formatNumberToReais } from '../../../utils/MoneyFormat';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import ModalFeedBack from '../../../components/ModalFeedBack';
import ModalAction from '../../../components/ModalAction';
import {
  sxButtonGerarBoletPrincipal,
  sxTable,
  sxTablePrincipal
} from './sxProps';
import { base64toBlob } from '../../../utils/Base64ToBlob';
import { downloadBoleto, generateNfeLink } from '../../../services/SegundaViaExternoService';

interface TabelaPrincipalMensalidadesProps {
  mensalidades: Boleto[];
  isPessoaFisica: boolean;
  codigoEmpresa?: string;
}

const TabelaPrincipalMensalidades: React.FC<TabelaPrincipalMensalidadesProps> = ({ mensalidades, isPessoaFisica, codigoEmpresa }) => {
  const [loadingBoletos, setLoadingBoletos] = useState<Record<string, boolean>>({});
  const [nfeLinkMap, setNfeLinkMap] = useState<Record<string, string | null | undefined>>({});
  const [mensalidadesState, setMensalidadesState] = useState<Boleto[]>(mensalidades);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [selectedMensalidade, setSelectedMensalidade] = useState<Boleto | null>(null);

  const accessibility = useAccessibility();
  const { textStyleAccessibility, contrastStyle, contrastBorderAccessibility } = useAccessibility();

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const checkboxRefs = useRef<(HTMLInputElement | null)[]>([]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontFamily: 'Unimed-Sans',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    ...textStyleAccessibility
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#F9F9F9'
    },
  }));


  const handleGerarBoleto = async (mensalidade: Boleto) => {
    const boletoKey = `${mensalidade.id}-${mensalidade.vencimento}-${mensalidade.valorCorrigido.toString()}`;
    setLoadingBoletos(prev => ({ ...prev, [boletoKey]: true }));
    try {
      await downloadBoleto(mensalidade, codigoEmpresa, isPessoaFisica);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Erro ao fazer download do boleto');
      setErrorModal(true);
    } finally {
      setLoadingBoletos(prev => ({ ...prev, [boletoKey]: false }));
    }
  };

  const handleClickGerarBoleto = (mensalidade: Boleto) => {
    setSelectedMensalidade(mensalidade);
    setConfirmModal(true);
  };

  const handleCancelModal = () => {
    setConfirmModal(false);
    setSelectedMensalidade(null);
  };

  const handleConfirmGerarBoleto = async () => {
    if (!selectedMensalidade) {
      return;
    }

    setConfirmModal(false);
    await handleGerarBoleto(selectedMensalidade);
    setSelectedMensalidade(null);
  };

  const abrirPdf = (base64: string) => {
    const blob = base64toBlob(base64);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  };

  useEffect(() => {
    setMensalidadesState(mensalidades);
  }, [mensalidades]);

  const handleCloseErrorModal = () => {
    setErrorModal(false);
  };

  const getRowAriaLabel = (mensalidade: Boleto) => {
    return `Boleto com vencimento em ${mensalidade.vencimento}, valor ${formatNumberToReais(mensalidade.valor)}`;
  };

  const isLoadingBoleto = (mensalidade: Boleto) => {
    const boletoKey = `${mensalidade.id}-${mensalidade.vencimento}-${mensalidade.valorCorrigido.toString()}`;
    return loadingBoletos[boletoKey] || false;
  };


  useEffect(() => {
    if (isPessoaFisica || !mensalidadesState.length) {
      return;
    }

    const preloadNfeLinks = async () => {
      const initialState: Record<string, string | null | undefined> = {};

      mensalidadesState.forEach((mensalidade) => {
        if (mensalidade.numero_titulo) {
          initialState[mensalidade.numero_titulo] = undefined;
        }
      });

      setNfeLinkMap((prev) => ({ ...prev, ...initialState }));

      const links: Record<string, string | null> = {};

      await Promise.all(
        mensalidadesState.map(async (mensalidade) => {
          if (!mensalidade.numero_titulo) {
            return;
          }

          try {
            const response = await generateNfeLink({
              numeroTitulo: mensalidade.numero_titulo,
              idContrato: codigoEmpresa,
            });

            links[mensalidade.numero_titulo] = response.link || null;
          } catch {
            links[mensalidade.numero_titulo] = null;
          }
        }),
      );

      setNfeLinkMap((prev) => ({ ...prev, ...links }));
    };

    preloadNfeLinks();
  }, [isPessoaFisica, mensalidadesState, codigoEmpresa]);

  const handleOpenNfeLink = (numeroTitulo?: string) => {
    if (!numeroTitulo) {
      return;
    }

    const link = nfeLinkMap[numeroTitulo];

    if (link) {
      window.open(link, '_blank');
    }
  };

  return (
    <div className='container-tabela-mensalidades'>

      <ModalFeedBack
        showModal={errorModal}
        handleShowModal={handleCloseErrorModal}
        icon={'error'}
        textButton={'Fechar'}
        title={'Erro'}
        subTitle={errorMessage}
        showButtonModal
        handleButtonModal={handleCloseErrorModal}
        classNameModal="modal-feedback-error"
      />
      <div
        ref={tableWrapperRef}
        style={{ outline: 'none' }}
      >
        <TableContainer
          className='container-table-principal'
          component={Paper}
          elevation={3}
          sx={sxTablePrincipal}
          style={{ ...contrastBorderAccessibility, ...contrastStyle }}
        >
          <Table stickyHeader size="medium" aria-label="tabela de boletos">
            <TableHead>
              <TableRow>
                <StyledTableCell style={textStyleAccessibility}><b>Vencimento</b></StyledTableCell>
                <StyledTableCell align="center"><b>Valor</b></StyledTableCell>
                <StyledTableCell align="center"><b></b></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mensalidadesState.map((mensalidade, index) => (
                <StyledTableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <StyledTableCell component="th" scope="row">
                    {mensalidade.vencimento}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {formatNumberToReais(mensalidade.valor)}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '9px', justifyContent: 'center' }}>
                      {!isPessoaFisica && mensalidade.numero_titulo && (() => {
                        const nfeValue = nfeLinkMap[mensalidade.numero_titulo];
                        const isNfeLoading = mensalidade.numero_titulo in nfeLinkMap && nfeValue === undefined;

                        if (isNfeLoading) {
                          return (
                            <DefaultButton
                              variant="outlined"
                              sx={sxButtonGerarBoletPrincipal}
                              disabled
                            >
                              <CircularProgress size={20} />
                            </DefaultButton>
                          );
                        }

                        if (!nfeValue) {
                          return null;
                        }

                        return (
                          <DefaultButton
                            variant="outlined"
                            sx={sxButtonGerarBoletPrincipal}
                            style={accessibility.buttonStyleAccessibility}
                            onClick={() => handleOpenNfeLink(mensalidade.numero_titulo)}
                          >
                            <b style={accessibility.buttonTextStyleAccessibility}>CONSULTAR NFe</b>
                          </DefaultButton>
                        );
                      })()}

                      <DefaultButton
                        ref={(el) => (buttonRefs.current[index] = el)}
                        variant="outlined"
                        sx={sxButtonGerarBoletPrincipal}
                        style={accessibility.buttonStyleAccessibility}
                        onClick={() => handleClickGerarBoleto(mensalidade)}
                        disabled={isLoadingBoleto(mensalidade)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (index < mensalidadesState.length - 1) {
                              buttonRefs.current[index + 1]?.focus();
                            }
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (index > 0) {
                              buttonRefs.current[index - 1]?.focus();
                            }
                          }
                        }}
                        aria-label={`${getRowAriaLabel(mensalidade)}, pressione Enter para gerar boleto`}
                      >
                        {isLoadingBoleto(mensalidade) ? <CircularProgress size={20} /> : <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">GERAR BOLETO</b>}
                      </DefaultButton>
                    </div>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <ModalAction
        showModal={confirmModal}
        handleShowModal={handleCancelModal}
        icon="warning"
        maxWidth="465px"
        title="Atenção!"
        colorContainedButton='success'
        subTitle={
          `Para sua segurança, o arquivo do boleto está protegido. Para abri-lo, será necessário digitar os <b>08 primeiros dígitos do ${codigoEmpresa ? 'CNPJ' : 'CPF'}</b> do titular do plano. Ele abrirá numa nova aba, então <b>verifique se o bloqueador está desativado</b>.`
        }
        textOutlinedButton="CANCELAR"
        textContainedButton="GERAR BOLETO"
        handleOutlinedButton={handleCancelModal}
        handleContainedButton={handleConfirmGerarBoleto}
      />
    </div>
  );
};

export default TabelaPrincipalMensalidades;







